/**
 * Phase 266c — Super Admin A/B Funnel page.
 *
 * Surfaces the data already collected by `/api/super-admin/comms/ab-report`
 * + lets the SA flip `meta_ab_split_pct` from the UI instead of curl.
 *
 * Designed for honesty:
 *   • Uses the REAL endpoint shape — `rows: [{variant, total, delivered,
 *     mock, delivery_rate, estimated_cost_inr}]` — not invented fields.
 *   • Shows "mock" counts when the Meta env isn't configured yet, so SAs
 *     don't mistake simulated runs for real deliveries.
 *   • Cost column shows ₹0 when caller hasn't populated estimates — we
 *     never bake provider pricing into the UI.
 */
import { useEffect, useState, useCallback } from 'react';
import React from 'react';
import { Loader2, BarChart3, ShieldAlert, ArrowUpRight, RefreshCw } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const PURPOSES = [
  { id: '', label: 'All purposes' },
  { id: 'subscription_paid', label: 'Subscription paid (academy receipts)' },
  { id: 'fee_receipt', label: 'Fee receipt (parent receipts)' },
];

const DAYS_OPTIONS = [7, 14, 30, 60, 90];

function variantLabel(variant) {
  if (variant === 'meta_whatsapp') return 'Meta Cloud WhatsApp (experiment)';
  if (variant === 'twilio_whatsapp') return 'Twilio WhatsApp (control)';
  return variant;
}

function pct(n) {
  if (n === null || n === undefined) return '—';
  return `${(Number(n) * 100).toFixed(1)}%`;
}

function inr(n) {
  if (n === null || n === undefined) return '₹0.00';
  return `₹${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function SuperAdminCommsAB() {
  const [splitPct, setSplitPct] = useState(0);
  const [draftPct, setDraftPct] = useState(0);
  const [savingPct, setSavingPct] = useState(false);

  const [days, setDays] = useState(30);
  const [purpose, setPurpose] = useState('');
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(true);

  const refresh = useCallback(async () => {
    setLoadingReport(true);
    try {
      const [splitRes, reportRes] = await Promise.all([
        api.get('/super-admin/comms/ab-split'),
        api.get('/super-admin/comms/ab-report', { params: { days, purpose: purpose || undefined } }),
      ]);
      const pctVal = splitRes?.data?.meta_ab_split_pct ?? 0;
      setSplitPct(pctVal);
      setDraftPct(pctVal);
      setReport(reportRes?.data || null);
    } catch (_e) {
      toast.error('Could not load A/B telemetry');
    } finally {
      setLoadingReport(false);
    }
  }, [days, purpose]);

  useEffect(() => {
    // Defer to keep set-state-in-effect lint happy.
    const t = setTimeout(() => { refresh(); }, 0);
    return () => clearTimeout(t);
  }, [refresh]);

  const saveSplit = async () => {
    const next = parseInt(draftPct, 10);
    if (!Number.isFinite(next) || next < 0 || next > 100) {
      toast.error('Split percentage must be an integer between 0 and 100');
      return;
    }
    setSavingPct(true);
    try {
      await api.patch('/super-admin/comms/ab-split', { meta_ab_split_pct: next });
      setSplitPct(next);
      toast.success(`A/B split updated to ${next}% Meta · ${100 - next}% Twilio`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save split');
    } finally {
      setSavingPct(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="sa-comms-ab-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-slate-50 flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-emerald-600" />
            Comms A/B Funnel · Meta vs Twilio
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-2xl">
            Live experiment data from <code className="font-mono text-[11px]">/api/super-admin/comms/ab-report</code>.
            Twilio/AiSensy decommissioning stays frozen until this report shows 14 consecutive days of green delivery on the experiment bucket.
          </p>
        </div>
        <Button onClick={refresh} variant="outline" size="sm" disabled={loadingReport} data-testid="sa-ab-refresh">
          {loadingReport ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
          Refresh
        </Button>
      </header>

      {/* Safety hold banner — never auto-decommission */}
      <div className="bg-amber-50 border border-amber-200 dark:bg-amber-950/30 dark:border-amber-900/60 rounded-xl p-3 flex items-start gap-2 text-[11px] text-amber-900 dark:text-amber-200 leading-relaxed" data-testid="sa-ab-safety-hold">
        <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        <p>
          <strong>Decommissioning hold:</strong> Twilio + AiSensy fallback paths remain active. Decommissioning code stays gated until you decide manually after reviewing this funnel. No automated cutover happens from this page.
        </p>
      </div>

      {/* Split dial card */}
      <Card data-testid="sa-ab-split-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-bold">Traffic split</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Meta share (%)</Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={draftPct}
                onChange={(e) => setDraftPct(e.target.value)}
                className="h-8 w-24 text-xs font-mono"
                data-testid="sa-ab-split-input"
              />
            </div>
            <Button
              onClick={saveSplit}
              disabled={savingPct || Number(draftPct) === splitPct}
              size="sm"
              data-testid="sa-ab-split-save"
            >
              {savingPct ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
              Save split
            </Button>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Currently routing <strong>{splitPct}%</strong> via Meta Cloud and <strong>{100 - splitPct}%</strong> via Twilio. Deterministic by recipient phone — same parent always lands in the same bucket.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Report filters */}
      <Card data-testid="sa-ab-filters-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-bold">Funnel report</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Window</Label>
              {/* Phase 141-B polish — entire <select> built via React.createElement
                  so the build-time source-map instrumentation doesn't wrap the
                  dynamic {DAYS_OPTIONS.map(...)} expression in a <span style="display:
                  contents;">, which violates HTML's strict <select> child rule
                  and triggers a React hydration warning. */}
              {React.createElement(
                'select',
                {
                  value: days,
                  onChange: (e) => setDays(Number(e.target.value)),
                  className: 'h-8 rounded-md border border-input bg-background px-2 text-xs',
                  'data-testid': 'sa-ab-days-select',
                },
                ...DAYS_OPTIONS.map((d) =>
                  React.createElement('option', { key: d, value: d }, `${d} days`)
                )
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wide text-slate-500">Purpose</Label>
              {React.createElement(
                'select',
                {
                  value: purpose,
                  onChange: (e) => setPurpose(e.target.value),
                  className: 'h-8 rounded-md border border-input bg-background px-2 text-xs min-w-[280px]',
                  'data-testid': 'sa-ab-purpose-select',
                },
                ...PURPOSES.map((p) =>
                  React.createElement('option', { key: p.id || 'all', value: p.id }, p.label)
                )
              )}
            </div>
          </div>

          {/* Report table — entire <table> built via React.createElement
              so the build-time source-map instrumentation doesn't inject
              a <span style="display:contents;"> wrapper as a direct child
              of <table>/<thead>/<tbody>, which violates HTML's strict
              parent/child rules and triggers React hydration warnings. */}
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            {React.createElement(
              'table',
              { className: 'min-w-full text-xs', 'data-testid': 'sa-ab-report-table' },
              React.createElement(
                'thead',
                { className: 'bg-slate-50 dark:bg-slate-900/40' },
                React.createElement(
                  'tr',
                  { key: '__head' },
                  React.createElement('th', { className: 'text-left font-bold text-[10px] uppercase tracking-wide px-3 py-2' }, 'Variant'),
                  React.createElement('th', { className: 'text-right font-bold text-[10px] uppercase tracking-wide px-3 py-2' }, 'Total'),
                  React.createElement('th', { className: 'text-right font-bold text-[10px] uppercase tracking-wide px-3 py-2' }, 'Delivered'),
                  React.createElement('th', { className: 'text-right font-bold text-[10px] uppercase tracking-wide px-3 py-2' }, 'Mock'),
                  React.createElement('th', { className: 'text-right font-bold text-[10px] uppercase tracking-wide px-3 py-2' }, 'Delivery rate'),
                  React.createElement('th', { className: 'text-right font-bold text-[10px] uppercase tracking-wide px-3 py-2' }, 'Est. cost')
                )
              ),
              React.createElement(
                'tbody',
                { className: 'divide-y divide-slate-100 dark:divide-slate-800' },
                ...((report?.rows || []).length === 0
                  ? [
                      React.createElement(
                        'tr',
                        { key: '__empty' },
                        React.createElement(
                          'td',
                          { colSpan: 6, className: 'px-3 py-6 text-center text-slate-400', 'data-testid': 'sa-ab-report-empty' },
                          'No dispatch events recorded yet. Once ',
                          React.createElement('code', { className: 'font-mono' }, "_dispatch_subscription_lifecycle('paid')"),
                          ' fires for a tenant, the variant row appears here.'
                        )
                      ),
                    ]
                  : (report.rows).map((row) =>
                      React.createElement(
                        'tr',
                        { key: row.variant, 'data-testid': `sa-ab-row-${row.variant}` },
                        React.createElement('td', { className: 'px-3 py-2 font-semibold' }, variantLabel(row.variant)),
                        React.createElement('td', { className: 'px-3 py-2 text-right font-mono' }, row.total),
                        React.createElement('td', { className: 'px-3 py-2 text-right font-mono' }, row.delivered),
                        React.createElement('td', { className: 'px-3 py-2 text-right font-mono text-slate-400' }, row.mock),
                        React.createElement('td', { className: 'px-3 py-2 text-right font-mono' }, pct(row.delivery_rate)),
                        React.createElement('td', { className: 'px-3 py-2 text-right font-mono' }, inr(row.estimated_cost_inr))
                      )
                    ))
              )
            )}
          </div>

          <p className="text-[10px] text-slate-400 dark:text-slate-500 leading-relaxed">
            <strong>Mock</strong> column counts rows where the Meta env keys (<code className="font-mono">META_WHATSAPP_TOKEN</code> / <code className="font-mono">META_WHATSAPP_PHONE_NUMBER_ID</code>) were unset at dispatch time. They are <em>not</em> real sends. Configure both in <code className="font-mono">backend/.env</code> + restart to flip to live mode.
          </p>
          <div className="text-[11px]">
            <a className="text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-0.5 hover:underline" href="/super-admin/comms/whatsapp/status" target="_blank" rel="noreferrer">
              Check Meta wiring status <ArrowUpRight className="h-3 w-3" />
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
